import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Circle,
  Link2,
  Loader2,
  Map,
  Sparkles,
  Trophy,
  X,
} from "lucide-react";
import {
  completeOnboarding,
  fetchOnboarding,
  ONBOARDING_QUERY_KEY,
  type OnboardingState,
  type OnboardingStep,
  type OnboardingStepKey,
} from "../api/onboarding";
import { resendVerification } from "../api/auth";
import { isPlaceholderEmail } from "@atlas/shared";
import { useAuth } from "../hooks/useAuth";
import { scrollToEmailCapture } from "./EmailCaptureCard";
import { Button } from "./Button";
import { ProgressBar } from "./ProgressBar";
import { Skel } from "./Skeleton";
import { useStravaConnectionsFull } from "../hooks/useFeatureFlags";

// First-run "Getting started" strip (spec 15, compacted 2026-10-09 per
// spec 29). One card the height of a single step under the dashboard hero:
// progress dots, the next step as the headline, its one CTA, and a chevron
// that expands the full checklist. Carries the verify-email step itself, so
// EmailCaptureCard defers its unverified case while this is up. Every step
// is derived server-side; this component only decides what to say and where
// the single primary CTA points. Polls while an import is in flight.
// Mobile port: packages/mobile/src/components/OnboardingCard.tsx.

const POLL_MS = 10_000;

const SPIN_CSS = `
@keyframes atlas-onboarding-spin { to { transform: rotate(360deg); } }
.atlas-onboarding-spin { animation: atlas-onboarding-spin 1s linear infinite; }
@media (prefers-reduced-motion: reduce) { .atlas-onboarding-spin { animation: none; } }
`;
function injectSpinCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById("atlas-onboarding-css")) return;
  const s = document.createElement("style");
  s.id = "atlas-onboarding-css";
  s.textContent = SPIN_CSS;
  document.head.appendChild(s);
}

const STEP_LABEL: Record<OnboardingStepKey, string> = {
  verify_email: "Verify your email",
  connect_source: "Connect a source",
  first_activity_processed: "Import your first activity",
  first_unlock: "Make your first find",
  set_up_profile: "Choose a username",
  find_explorers: "Follow an explorer",
};

interface StepAction {
  label: string;
  to?: string;
  onClick?: () => void;
}

function stepAction(
  step: OnboardingStep,
  resend: () => void,
  resendState: "idle" | "sending" | "sent",
  placeholderEmail = false,
): StepAction | null {
  switch (step.key) {
    case "verify_email":
      // Strava-only accounts have no address to resend to: point at the
      // "Add your email" card that sits directly above this one.
      if (placeholderEmail) {
        return { label: "Add an email", onClick: scrollToEmailCapture };
      }
      return {
        label: resendState === "sent" ? "Email sent" : resendState === "sending" ? "Sending" : "Resend email",
        onClick: resendState === "idle" ? resend : undefined,
      };
    case "connect_source":
      return { label: "Connect a source", to: "/connected-sources" };
    case "first_activity_processed":
      return null;
    case "first_unlock":
      return step.detail ? { label: "Upload more", to: "/connected-sources" } : null;
    case "set_up_profile":
      return { label: "Choose a username", to: "/profile" };
    case "find_explorers":
      return { label: "Browse the feed", to: "/feed" };
  }
}

function shouldPoll(data: OnboardingState | undefined): number | false {
  if (!data || data.completedAt) return false;
  return data.processing.queued > 0 || data.processing.processing > 0 ? POLL_MS : false;
}

// ─── How it works ─────────────────────────────────────────────────────────────

const PANELS = [
  {
    icon: Link2,
    title: "Connect",
    body: "Link Strava, Apple Health or Health Connect, or upload GPX files. Atlas imports your past walks, runs and rides. Nothing is recorded live and nothing is planned for you.",
  },
  {
    icon: Map,
    title: "We match against the map",
    body: "Each track is compared with curated peaks, trail sections, landmarks and regions. A summit within 75 m, a section walked end to end, a landmark passed close by: all of it counts.",
  },
  {
    icon: Trophy,
    title: "Your collections fill in",
    body: "Finds land in your collections, unlock achievements and grow your Outdoor Score. Come back after each outing and see what changed.",
  },
];

function HowItWorksModal({ onClose }: { onClose: () => void }) {
  const [index, setIndex] = React.useState(0);
  const panel = PANELS[index] ?? PANELS[0]!;
  const Icon = panel.icon;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => Math.min(PANELS.length - 1, i + 1));
      if (e.key === "ArrowLeft") setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--scrim)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1100,
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="how-it-works-title"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 440,
          width: "100%",
          boxShadow: "var(--shadow-xl)",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span className="eyebrow" style={{ fontSize: 11 }}>
            How it works · {index + 1} of {PANELS.length}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer", padding: 4 }}
          >
            <X size={16} />
          </button>
        </div>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "var(--radius-md)",
            background: "var(--accent-soft)",
            border: "1px solid var(--border-gold)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--gold-400)",
          }}
        >
          <Icon size={22} />
        </div>
        <h2 id="how-it-works-title" style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: 0 }}>
          {panel.title}
        </h2>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--text-secondary)" }}>{panel.body}</p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
          <div style={{ display: "flex", gap: 6 }} aria-hidden="true">
            {PANELS.map((_, i) => (
              <span
                key={i}
                style={{
                  width: i === index ? 18 : 6,
                  height: 6,
                  borderRadius: "var(--radius-pill)",
                  background: i === index ? "var(--accent)" : "var(--border-strong)",
                  transition: "width 0.15s ease",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setIndex(index - 1)}>
                Back
              </Button>
            )}
            {index < PANELS.length - 1 ? (
              <Button variant="secondary" size="sm" onClick={() => setIndex(index + 1)}>
                Next
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Strip ────────────────────────────────────────────────────────────────────

function StepRow({ step, isNext, waiting }: { step: OnboardingStep; isNext: boolean; waiting: boolean }) {
  return (
    <li style={{ display: "flex", alignItems: "center", gap: 12, padding: "6px 0", minWidth: 0 }}>
      <span
        aria-hidden="true"
        style={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: step.done ? "var(--spruce-500)" : "transparent",
          color: step.done ? "var(--bg-app)" : isNext ? "var(--gold-400)" : "var(--text-muted)",
        }}
      >
        {step.done ? <Check size={13} strokeWidth={3} /> : waiting ? <Loader2 size={15} className="atlas-onboarding-spin" /> : <Circle size={14} />}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: isNext ? 600 : 500,
            color: step.done ? "var(--text-muted)" : "var(--text-primary)",
            textDecoration: step.done ? "line-through" : "none",
            textDecorationColor: "var(--border-strong)",
          }}
        >
          {STEP_LABEL[step.key]}
          {step.optional && !step.done && (
            <span style={{ marginLeft: 8, fontSize: 11, color: "var(--text-muted)", fontWeight: 500 }}>optional</span>
          )}
        </div>
        {step.detail && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 1 }}>{step.detail}</div>}
      </div>
    </li>
  );
}

function ActionButton({ action, variant }: { action: StepAction; variant: "primary" | "secondary" | "ghost" }) {
  if (action.to) {
    return (
      <Link to={action.to} style={{ textDecoration: "none" }}>
        <Button variant={variant} size="sm">{action.label}</Button>
      </Link>
    );
  }
  return (
    <Button variant={variant} size="sm" onClick={action.onClick} disabled={!action.onClick}>
      {action.label}
    </Button>
  );
}

export function OnboardingCard() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const stravaFull = useStravaConnectionsFull();
  const placeholderEmail = isPlaceholderEmail(user?.email);
  React.useEffect(injectSpinCss, []);
  const [expanded, setExpanded] = React.useState(false);
  const [showHow, setShowHow] = React.useState(false);
  const [resendState, setResendState] = React.useState<"idle" | "sending" | "sent">("idle");

  const query = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: fetchOnboarding,
    staleTime: 30_000,
    refetchInterval: (q) => shouldPoll(q.state.data),
  });

  const completeMutation = useMutation({
    mutationFn: completeOnboarding,
    onMutate: () => {
      queryClient.setQueryData<OnboardingState>(ONBOARDING_QUERY_KEY, (prev) =>
        prev ? { ...prev, completedAt: new Date().toISOString() } : prev,
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_QUERY_KEY });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendVerification,
    onMutate: () => setResendState("sending"),
    onSuccess: () => setResendState("sent"),
    onError: () => setResendState("idle"),
  });

  // Refresh the dashboard's own panels once the first import lands, so the
  // score and collections around this strip catch up without a reload.
  const firstProcessed = query.data?.steps.find((s) => s.key === "first_activity_processed")?.done ?? false;
  const firstUnlock = query.data?.steps.find((s) => s.key === "first_unlock")?.done ?? false;
  const prevRef = React.useRef({ firstProcessed, firstUnlock });
  React.useEffect(() => {
    const prev = prevRef.current;
    if ((firstProcessed && !prev.firstProcessed) || (firstUnlock && !prev.firstUnlock)) {
      void queryClient.invalidateQueries({ queryKey: ["profile/stats"] });
      void queryClient.invalidateQueries({ queryKey: ["collections"] });
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
    }
    prevRef.current = { firstProcessed, firstUnlock };
  }, [firstProcessed, firstUnlock, queryClient]);

  if (query.isLoading) {
    return <Skel height="64px" />;
  }
  const data = query.data;
  if (!data || data.completedAt) return null;

  const steps = data.steps;
  const required = steps.filter((s) => !s.optional);
  const doneCount = required.filter((s) => s.done).length;
  const next = steps.find((s) => !s.done && !s.optional) ?? steps.find((s) => !s.done) ?? null;
  const allRequiredDone = required.every((s) => s.done);
  const { processing } = data;
  const inFlight = processing.queued > 0 || processing.processing > 0;
  const total = processing.done + processing.processing;
  // Matching in progress takes over the headline: nothing useful can happen
  // until it lands, and the count is the news.
  const matching = inFlight && (next?.key === "first_activity_processed" || next?.key === "first_unlock");

  let headline: string;
  let hint: string | null = null;
  let action: StepAction | null = null;
  let secondary: StepAction | null = null;

  if (matching) {
    headline = "Matching your walks";
    hint =
      processing.queued > 0 && total === 0
        ? "Sync in progress. You can leave this page."
        : `${processing.done.toLocaleString()} of ${total.toLocaleString()} so far. You can leave this page.`;
  } else if (!next) {
    headline = "All set";
    hint = "Everything on the list is done.";
  } else {
    headline = STEP_LABEL[next.key];
    action = stepAction(next, () => resendMutation.mutate(), resendState, placeholderEmail);
    switch (next.key) {
      case "verify_email":
        hint = placeholderEmail
          ? "Add an email in the card above so Atlas can reach you."
          : resendState === "sent"
            ? "Link sent. Check your inbox and spam."
            : `We sent a link to ${user?.email ?? "your address"}.`;
        if (!placeholderEmail) secondary = { label: "Change address", to: "/profile" };
        break;
      case "connect_source":
        hint = stravaFull
          ? "Strava connections are temporarily full. Upload a GPX file, or use Apple Health or Health Connect in the app."
          : "Strava, Apple Health, Health Connect or a GPX file. The matching runs in the background.";
        break;
      case "first_activity_processed":
        hint = next.detail ?? "Your first walk appears here once it arrives.";
        break;
      case "first_unlock":
        hint = next.detail ?? "Finds land here as soon as a track matches the map.";
        break;
      case "set_up_profile":
        hint = next.detail;
        break;
      case "find_explorers":
        hint = "Optional. See what other people are finding.";
        break;
    }
  }

  return (
    <section
      aria-label="Getting started"
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "var(--shadow-sm), var(--ring-top)",
        padding: "12px 16px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: "none",
          border: "none",
          padding: 0,
          cursor: "pointer",
          color: "var(--text-muted)",
          width: "100%",
          textAlign: "left",
        }}
      >
        <span aria-hidden="true" style={{ display: "flex", gap: 4, marginRight: 4 }}>
          {required.map((s) => (
            <span
              key={s.key}
              style={{ width: 6, height: 6, borderRadius: 3, background: s.done ? "var(--accent)" : "var(--border-strong)" }}
            />
          ))}
        </span>
        <span className="eyebrow" style={{ fontSize: 10 }}>
          Getting started · {doneCount} of {required.length}
        </span>
        <span style={{ flex: 1 }} />
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {matching && <Loader2 size={15} className="atlas-onboarding-spin" style={{ color: "var(--sky-500)", flexShrink: 0 }} />}
          {firstUnlock && !matching && next?.key !== "first_unlock" && (
            <Sparkles size={15} style={{ color: "var(--gold-400)", flexShrink: 0 }} />
          )}
          <span style={{ font: "600 16px/1.3 var(--font-display)", color: "var(--text-primary)" }}>{headline}</span>
        </div>
        {hint && <span style={{ fontSize: 13, lineHeight: 1.45, color: "var(--text-secondary)" }}>{hint}</span>}
      </div>

      {matching && <ProgressBar value={total > 0 ? (processing.done / total) * 100 : 0} />}

      <div className="r-wrap" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {action && <ActionButton action={action} variant={firstUnlock ? "secondary" : "primary"} />}
        {secondary && <ActionButton action={secondary} variant="ghost" />}
        {firstUnlock && !matching && (
          <Link to="/achievements" style={{ textDecoration: "none" }}>
            <Button variant="ghost" size="sm">See what you found</Button>
          </Link>
        )}
        <span style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending}>
          {allRequiredDone ? "Done" : "Skip for now"}
        </Button>
      </div>

      {expanded && (
        <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 10, marginTop: 2, display: "flex", flexDirection: "column", gap: 10 }}>
          <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            {steps.map((step) => (
              <StepRow
                key={step.key}
                step={step}
                isNext={next?.key === step.key}
                waiting={inFlight && next?.key === step.key && step.key === "first_activity_processed"}
              />
            ))}
          </ol>
          {processing.failed > 0 && (
            <p style={{ margin: 0, fontSize: 12, color: "var(--coral-500)" }}>
              A sync didn't finish. Check{" "}
              <Link to="/connected-sources" style={{ color: "inherit" }}>connected sources</Link> and try again.
            </p>
          )}
          <button
            type="button"
            onClick={() => setShowHow(true)}
            style={{ background: "none", border: "none", padding: 0, alignSelf: "flex-start", fontSize: 13, color: "var(--text-secondary)", textDecoration: "underline", cursor: "pointer" }}
          >
            How it works
          </button>
        </div>
      )}

      {showHow && <HowItWorksModal onClose={() => setShowHow(false)} />}
    </section>
  );
}
